import {
  toBrowserDateOnly,
  toUtcDateOnly,
  type AuthenticatedOptions,
  type ListGenericTransactionsData,
  type ListIncomingTransfersData,
  type ListOutgoingTransfersData,
} from '@peanuds/shared';

export const DEFAULT_TRANSFER_DATE_FROM = '2024-01-01';
export const DEFAULT_PAGE_NUMBER = 1;
export const DEFAULT_PAGE_SIZE = 100;
export const DEFAULT_LIMIT = 100;
export const DEFAULT_OFFSET = 0;

type CommonRequestOptions = {
  transferDateFrom?: string;
  transferDateTo?: string;
  pageNumber?: number;
  pageSize?: number;
  limit?: number;
  offset?: number;
};

const resolvePaginationQuery = ({
  pageNumber,
  pageSize,
  limit = DEFAULT_LIMIT,
  offset = DEFAULT_OFFSET,
}: CommonRequestOptions) => {
  const resolvedLimit = pageSize ?? limit;

  if (pageNumber === undefined) {
    return {
      limit: resolvedLimit,
      offset,
    };
  }

  // Page-based controls take precedence over raw offset values so future
  // pagination UIs can pass pageNumber/pageSize without needing a separate
  // request-shaping path.
  return {
    limit: resolvedLimit,
    offset: Math.max(pageNumber - 1, 0) * resolvedLimit,
  };
};

const resolveTransferDateTo = (
  transferDateTo?: string,
  query?: { transferDateTo?: string },
): string => {
  return transferDateTo ?? query?.transferDateTo ?? toBrowserDateOnly();
};

const toUtcDateTimeBoundary = (value: string, boundary: 'start' | 'end'): string => {
  if (value.includes('T')) {
    return value;
  }

  return boundary === 'start' ? `${value}T00:00:00Z` : `${value}T23:59:59Z`;
};

const resolveCreationDateTimeFrom = (
  transferDateFrom: string,
  query?: { creationDateTimeFrom?: string },
): string => {
  return query?.creationDateTimeFrom ?? toUtcDateTimeBoundary(transferDateFrom, 'start');
};

const resolveCreationDateTimeTo = (
  transferDateTo?: string,
  query?: { creationDateTimeTo?: string },
): string => {
  const resolvedTransferDateTo = transferDateTo ?? toUtcDateOnly();

  return query?.creationDateTimeTo ?? toUtcDateTimeBoundary(resolvedTransferDateTo, 'end');
};

type IncomingRequestParams = CommonRequestOptions & {
  accountId: string;
  incomingQuery?: Partial<ListIncomingTransfersData['query']>;
};

type OutgoingRequestParams = CommonRequestOptions & {
  accountId?: string;
  outgoingQuery?: Partial<ListOutgoingTransfersData['query']>;
};

type GenericRequestParams = CommonRequestOptions & {
  accountId: string;
  genericQuery?: Partial<ListGenericTransactionsData['query']>;
};

type CombinedRequestParams = IncomingRequestParams & OutgoingRequestParams;

export const createIncomingTransfersOptions = ({
  accountId,
  transferDateFrom = DEFAULT_TRANSFER_DATE_FROM,
  transferDateTo,
  pageNumber,
  pageSize,
  limit,
  offset,
  incomingQuery = {},
}: IncomingRequestParams): AuthenticatedOptions<ListIncomingTransfersData> => {
  const paginationQuery = resolvePaginationQuery({
    pageNumber,
    pageSize,
    limit,
    offset,
  });
  const resolvedTransferDateTo = resolveTransferDateTo(transferDateTo, incomingQuery);

  return {
    query: {
      ...incomingQuery,
      accountId,
      transferDateFrom,
      transferDateTo: resolvedTransferDateTo,
      ...paginationQuery,
    },
  };
};

export const createOutgoingTransfersOptions = ({
  accountId,
  transferDateFrom = DEFAULT_TRANSFER_DATE_FROM,
  transferDateTo,
  pageNumber,
  pageSize,
  limit,
  offset,
  outgoingQuery = {},
}: OutgoingRequestParams): AuthenticatedOptions<ListOutgoingTransfersData> => {
  const paginationQuery = resolvePaginationQuery({
    pageNumber,
    pageSize,
    limit,
    offset,
  });
  const resolvedTransferDateTo = resolveTransferDateTo(transferDateTo, outgoingQuery);

  return {
    query: {
      ...outgoingQuery,
      ...(accountId ? { accountId } : {}),
      transferDateFrom,
      transferDateTo: resolvedTransferDateTo,
      ...paginationQuery,
    },
  };
};

export const createGenericTransactionsOptions = ({
  accountId,
  transferDateFrom = DEFAULT_TRANSFER_DATE_FROM,
  transferDateTo,
  pageNumber,
  pageSize,
  limit,
  offset,
  genericQuery = {},
}: GenericRequestParams): AuthenticatedOptions<ListGenericTransactionsData> => {
  const paginationQuery = resolvePaginationQuery({
    pageNumber,
    pageSize,
    limit,
    offset,
  });

  return {
    query: {
      ...genericQuery,
      accountId,
      creationDateTimeFrom: resolveCreationDateTimeFrom(transferDateFrom, genericQuery),
      creationDateTimeTo: resolveCreationDateTimeTo(transferDateTo, genericQuery),
      ...paginationQuery,
    },
  };
};

export const createTransactionsRequestOptions = (
  params: CombinedRequestParams,
): {
  incoming: AuthenticatedOptions<ListIncomingTransfersData>;
  outgoing: AuthenticatedOptions<ListOutgoingTransfersData>;
} => {
  return {
    incoming: createIncomingTransfersOptions(params),
    outgoing: createOutgoingTransfersOptions(params),
  };
};
